import type { Instant } from './types'

/**
 * Connectivity: who may SSH into which robot or office computer.
 *
 * One kind of rule: **who** (a person, or a team of people) may SSH into
 * **what** (a machine, or a group of machines), at a level, until removed or
 * until a date. That covers both ends of the fleet without two models:
 *
 *  - **Small:** Vaishanth × Office 3090. No teams, no groups — making a group
 *    of one is busywork.
 *  - **Large:** Field · Site A × Site A. At two hundred robots and thirty
 *    engineers nobody keeps six thousand person × robot rules right, so teams
 *    (a person can be in several) and groups (a machine can be in several)
 *    carry it. Two groups are automatic — every robot, every office computer —
 *    so a robot joining the fleet is covered by "All robots" unprompted.
 *  - **The exception:** Omar × R-147 until Friday. Any rule may end; one that
 *    does ends on its own.
 *
 * **Levels.** Every rule and direct grant says *how* the person gets in:
 * `full` logs in as the machine's main account (`nupeak`, which is in the
 * docker group and so effectively root), `restricted` as a separate account
 * with no docker and no sudo, for people who need the robot but not the core
 * code. Tailscale SSH enforces it by the account name alone. A machine without
 * a restricted account simply has no restricted login.
 *
 * Everything here is derived on read, never stored: who may reach a machine
 * is a function of these rows and `now`, so a direct grant ends at its end
 * date and a deactivated account loses everything, with nobody touching them.
 * `networkPolicy.ts` turns the answer into headscale's policy.
 */

export type MachineKind = 'robot' | 'computer'

export type AccessLevel = 'full' | 'restricted'

export interface Machine {
  readonly id: string
  /** What people call it: "R2", "Office 3090". */
  readonly name: string
  /** `tag:r2`. Unique; the machine's identity on the tailnet. */
  readonly tag: string
  readonly kind: MachineKind
  /** The Linux account a `full` login uses — `nupeak` on every robot today. */
  readonly sshUser: string
  /** The account a `restricted` login uses; null until the machine has one. */
  readonly restrictedUser: string | null
  /** The Kronos robot this is, when it is one. */
  readonly robotId: string | null
  /** A retired machine is in no group and reachable by nobody. */
  readonly retiredAt: Instant | null
}

/** The part of a Kronos account access needs. */
export interface AccessPerson {
  readonly id: string
  readonly email: string
  /** Deactivated accounts reach nothing, whatever their teams say. */
  readonly active: boolean
}

export interface Team {
  readonly id: string
  readonly name: string
}

export interface TeamMember {
  readonly teamId: string
  readonly userId: string
}

/**
 * `custom` groups hold exactly the machines an admin put in them;
 * `all_robots` and `all_computers` hold every live machine of that kind.
 */
export type GroupKind = 'custom' | 'all_robots' | 'all_computers'

export interface MachineGroup {
  readonly id: string
  readonly name: string
  readonly kind: GroupKind
}

export interface GroupMember {
  readonly groupId: string
  readonly machineId: string
}

/**
 * Who may SSH into what. Exactly one of `teamId`/`userId` is set (who), and
 * exactly one of `groupId`/`machineId` (what). Open rules only — a revoked one
 * is history and not part of the world.
 */
export interface AccessRule {
  readonly id: string
  readonly teamId: string | null
  readonly userId: string | null
  readonly groupId: string | null
  readonly machineId: string | null
  readonly level: AccessLevel
  readonly grantedAt: Instant
  /** Null: until removed. */
  readonly expiresAt: Instant | null
}

/** Everything access is computed from. */
export interface AccessWorld {
  readonly machines: readonly Machine[]
  readonly people: readonly AccessPerson[]
  readonly teams: readonly Team[]
  readonly teamMembers: readonly TeamMember[]
  readonly groups: readonly MachineGroup[]
  readonly groupMembers: readonly GroupMember[]
  readonly rules: readonly AccessRule[]
}

// --- groups --------------------------------------------------------------------

/** The live machines in a group, by id. */
export function machinesIn(world: Pick<AccessWorld, 'machines' | 'groupMembers'>, group: MachineGroup): readonly string[] {
  const live = world.machines.filter((m) => m.retiredAt === null)
  if (group.kind === 'all_robots') return live.filter((m) => m.kind === 'robot').map((m) => m.id)
  if (group.kind === 'all_computers') return live.filter((m) => m.kind === 'computer').map((m) => m.id)
  const ids = new Set(live.map((m) => m.id))
  return [...new Set(world.groupMembers.filter((g) => g.groupId === group.id && ids.has(g.machineId)).map((g) => g.machineId))]
}

// --- rules ------------------------------------------------------------------------

export type RuleState = 'active' | 'ended'

export function ruleState(rule: AccessRule, now: Instant): RuleState {
  return rule.expiresAt !== null && Date.parse(rule.expiresAt) <= Date.parse(now) ? 'ended' : 'active'
}

export type RuleRefusal = 'no_subject' | 'no_target' | 'person_deactivated' | 'already_over'

export const RULE_REFUSAL_MESSAGE: Readonly<Record<RuleRefusal, string>> = {
  no_subject: 'pick a person or a team',
  no_target: 'pick a machine or a group',
  person_deactivated: 'that account is deactivated — it cannot be given access',
  already_over: 'the end date has already passed',
}

export function refuseRule(input: {
  readonly subjectExists: boolean
  readonly targetExists: boolean
  /** Null when the subject is a team. */
  readonly person: AccessPerson | null
  readonly expiresAt: Instant | null
  readonly now: Instant
}): RuleRefusal | null {
  if (!input.subjectExists) return 'no_subject'
  if (!input.targetExists) return 'no_target'
  if (input.person && !input.person.active) return 'person_deactivated'
  if (input.expiresAt !== null && !(Date.parse(input.expiresAt) > Date.parse(input.now))) return 'already_over'
  return null
}

// --- who appears in Connectivity ------------------------------------------------

/** An admin's explicit choice for one person; null leaves it to the default. */
export type Visibility = 'shown' | 'hidden' | null

/**
 * Whether a person is listed in Connectivity. Most of Kronos — the intake desk,
 * technicians who only build — never needs SSH, and a roster of them buries
 * the people who do. So by default the list is engineers and admins, plus
 * anyone already on the network or already holding access; an admin's choice
 * overrides that either way. One exception the choice cannot override:
 * **someone who can get in is always listed.** A page that hides who can reach
 * a robot is worse than a long one.
 */
export function shownInConnectivity(input: {
  readonly roles: readonly string[]
  readonly onNetwork: boolean
  readonly hasAccess: boolean
  readonly choice: Visibility
}): boolean {
  if (input.hasAccess) return true
  if (input.choice !== null) return input.choice === 'shown'
  return input.onNetwork || input.roles.includes('engineer') || input.roles.includes('admin')
}

// --- names ---------------------------------------------------------------------

export type NameRefusal = 'empty' | 'too_long' | 'taken'

export const NAME_REFUSAL_MESSAGE: Readonly<Record<NameRefusal, string>> = {
  empty: 'give it a name',
  too_long: 'keep the name under 60 characters',
  taken: 'that name is already used',
}

/** A team or group name: present, short, and unique ignoring case. */
export function refuseName(name: string, taken: readonly string[]): NameRefusal | null {
  const n = name.trim()
  if (!n) return 'empty'
  if (n.length > 60) return 'too_long'
  if (taken.some((t) => t.trim().toLowerCase() === n.toLowerCase())) return 'taken'
  return null
}

// --- who may reach what --------------------------------------------------------

/**
 * One reason a person may reach a machine: the rule, and whether it came to
 * them through a team and to the machine through a group (null: directly).
 */
export interface AccessPath {
  readonly ruleId: string
  readonly teamId: string | null
  readonly groupId: string | null
  readonly level: AccessLevel
  readonly expiresAt: Instant | null
}

/**
 * Every reason every active person may reach every live machine right now:
 * `machineId → userId → paths`. The one function the policy, "Can they get
 * in?", the machine panel and the person panel all read, so they cannot
 * disagree about why somebody has access.
 */
export function accessPaths(world: AccessWorld, now: Instant): ReadonlyMap<string, ReadonlyMap<string, readonly AccessPath[]>> {
  const active = new Set(world.people.filter((p) => p.active).map((p) => p.id))
  const groups = new Map(world.groups.map((g) => [g.id, g]))
  const groupMachines = new Map<string, readonly string[]>()
  const membersOf = (groupId: string): readonly string[] => {
    const cached = groupMachines.get(groupId)
    if (cached) return cached
    const group = groups.get(groupId)
    const ids = group ? machinesIn(world, group) : []
    groupMachines.set(groupId, ids)
    return ids
  }

  const out = new Map<string, Map<string, AccessPath[]>>()
  const add = (machineId: string, userId: string, path: AccessPath): void => {
    if (!active.has(userId)) return
    const byUser = out.get(machineId) ?? new Map<string, AccessPath[]>()
    const list = byUser.get(userId) ?? []
    list.push(path)
    byUser.set(userId, list)
    out.set(machineId, byUser)
  }

  const live = new Set(world.machines.filter((m) => m.retiredAt === null).map((m) => m.id))
  for (const rule of world.rules) {
    if (ruleState(rule, now) !== 'active') continue
    const users = rule.teamId !== null ? world.teamMembers.filter((m) => m.teamId === rule.teamId).map((m) => m.userId) : rule.userId !== null ? [rule.userId] : []
    const machines = rule.groupId !== null ? membersOf(rule.groupId) : rule.machineId !== null && live.has(rule.machineId) ? [rule.machineId] : []
    const path: AccessPath = { ruleId: rule.id, teamId: rule.teamId, groupId: rule.groupId, level: rule.level, expiresAt: rule.expiresAt }
    for (const machineId of machines) for (const userId of new Set(users)) add(machineId, userId, path)
  }
  return out
}

/** Why `userId` may reach `machineId` — empty when they may not. */
export function whyCanReach(world: AccessWorld, userId: string, machineId: string, now: Instant): readonly AccessPath[] {
  return accessPaths(world, now).get(machineId)?.get(userId) ?? []
}

/** The highest level among some paths: full wins. */
export function levelOf(paths: readonly AccessPath[]): AccessLevel {
  return paths.some((p) => p.level === 'full') ? 'full' : 'restricted'
}

/** The Linux account a level logs into on a machine; null when it has none. */
export function loginFor(machine: Machine, level: AccessLevel): string | null {
  return level === 'full' ? machine.sshUser : machine.restrictedUser
}

/** One machine, one account, everyone who may log into it — what `buildPolicy` writes. */
export interface Login {
  readonly tag: string
  readonly user: string
  readonly emails: readonly string[]
}

/**
 * Who logs into what, as which account. Each person gets one login per
 * machine, at the highest level they hold there; a restricted path to a
 * machine with no restricted account gives no login rather than a wrong one.
 */
export function policyLogins(world: AccessWorld, now: Instant): readonly Login[] {
  const email = new Map(world.people.map((p) => [p.id, p.email.trim().toLowerCase()]))
  const machines = new Map(world.machines.map((m) => [m.id, m]))
  const out = new Map<string, { tag: string; user: string; emails: Set<string> }>()
  for (const [machineId, byUser] of accessPaths(world, now)) {
    const machine = machines.get(machineId)
    if (!machine) continue
    for (const [userId, paths] of byUser) {
      const user = loginFor(machine, levelOf(paths))
      const address = email.get(userId)
      if (!user || !address) continue
      const key = `${machine.tag}\n${user}`
      const login = out.get(key) ?? { tag: machine.tag, user, emails: new Set<string>() }
      login.emails.add(address)
      out.set(key, login)
    }
  }
  return [...out.values()].map((l) => ({ tag: l.tag, user: l.user, emails: [...l.emails].sort() }))
}

/** How many live machines each person may reach, by kind. */
export function reachCounts(world: AccessWorld, now: Instant): ReadonlyMap<string, { readonly robots: number; readonly computers: number }> {
  const kind = new Map(world.machines.map((m) => [m.id, m.kind]))
  const out = new Map<string, { robots: number; computers: number }>()
  for (const [machineId, byUser] of accessPaths(world, now)) {
    for (const userId of byUser.keys()) {
      const c = out.get(userId) ?? { robots: 0, computers: 0 }
      if (kind.get(machineId) === 'robot') c.robots++
      else c.computers++
      out.set(userId, c)
    }
  }
  return out
}
